import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, CheckCircle2, Loader2 } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { copyPageVariables } from '@/services/api/broadcast';
import { getPageVariables } from '@/services/api/connections';
import type { MetaPage, PageVariable } from '../types/meta.types';

interface PageVariablesCopyModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentPage: MetaPage;
    pages: MetaPage[];
    onSuccess: () => void;
}

export default function PageVariablesCopyModal({
    open,
    onOpenChange,
    currentPage,
    pages,
    onSuccess,
}: PageVariablesCopyModalProps) {
    const [sourcePageId, setSourcePageId] = useState<string>(() => String(currentPage.id));
    const [targetPageId, setTargetPageId] = useState<string>(() => {
        const others = pages.filter(p => p.id !== currentPage.id);
        return others.length > 0 ? String(others[0].id) : String(currentPage.id);
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { data: sourceVariables = [], isLoading: loadingVariables } = useQuery({
        queryKey: ['page-variables', sourcePageId],
        queryFn: () => getPageVariables(Number(sourcePageId)),
        enabled: open && !!sourcePageId,
    });

    // overrides: varKey -> value para a página de destino
    const [overrides, setOverrides] = useState<Record<string, string>>({});

    // Inicializa overrides com os valores da origem quando as variáveis carregam.
    // Depende do sourcePageId + assinatura serializada das variáveis
    // (sourceVariables é re-criado a cada render do useQuery; comparar
    // referência causaria loop infinito de re-render).
    const sourceVariablesSignature = (sourceVariables as PageVariable[])
        .map(v => `${v.key}=${v.value}`)
        .join('|');
    useEffect(() => {
        const initial: Record<string, string> = {};
        for (const v of sourceVariables as PageVariable[]) {
            initial[v.key] = v.value;
        }
        setOverrides(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourcePageId, sourceVariablesSignature]);

    // Valores efetivos: override se existir, senão valor da origem
    const effectiveOverrides = (sourceVariables as PageVariable[]).reduce<Record<string, string>>((acc, v) => {
        acc[v.key] = overrides[v.key] ?? v.value;
        return acc;
    }, {});

    const handleOverrideChange = (key: string, value: string) => {
        setOverrides(prev => ({ ...prev, [key]: value }));
    };

    const pageOptions = pages.map(p => ({
        value: String(p.id),
        label: p.page_name,
    }));

    const handleCopy = async () => {
        if (!sourcePageId || !targetPageId) return;
        const srcId = Number(sourcePageId);
        const tgtId = Number(targetPageId);
        if (srcId === tgtId) {
            setError('A página de origem e destino devem ser diferentes.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Envia todos os valores (origem + edições) para a página de destino
            const overridesForTarget = { ...effectiveOverrides };
            await copyPageVariables({
                source_page_id: srcId,
                target_page_ids: [tgtId],
                overrides_per_page: [overridesForTarget],
            });
            setDone(true);
            setTimeout(() => {
                onSuccess();
                onOpenChange(false);
            }, 800);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Erro ao copiar variáveis');
        } finally {
            setLoading(false);
        }
    };

    const targetPage = pages.find(p => p.id === Number(targetPageId));
    const canCopy = sourcePageId && targetPageId && sourcePageId !== targetPageId && sourceVariables.length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Copy className="h-5 w-5 text-blue-600" />
                        Copiar variáveis entre páginas
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5 py-2">
                    {/* Origem e destino */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Copiar de (origem)
                            </Label>
                            <SearchableSelect
                                value={sourcePageId}
                                onValueChange={setSourcePageId}
                                options={pageOptions}
                                placeholder="Selecione a página de origem"
                                searchPlaceholder="Buscar origem..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Para (destino)
                            </Label>
                            <SearchableSelect
                                value={targetPageId}
                                onValueChange={setTargetPageId}
                                options={pageOptions}
                                placeholder="Selecione a página de destino"
                                searchPlaceholder="Buscar destino..."
                            />
                        </div>
                    </div>

                    <p className="text-xs text-gray-500">
                        As variáveis da página de origem serão copiadas para a página de destino.
                        Você pode editar os valores abaixo antes de copiar.
                    </p>

                    {/* Variáveis editáveis */}
                    {loadingVariables ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                    ) : sourceVariables.length === 0 ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 text-sm text-amber-800">
                            A página de origem não possui variáveis cadastradas.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">
                                Valores para a página de destino ({targetPage?.page_name ?? '—'})
                            </p>
                            <div className="space-y-3 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/50 p-3">
                                {(sourceVariables as PageVariable[]).map((v) => (
                                    <div key={v.key} className="space-y-1">
                                        <Label className="text-xs font-mono text-gray-600">{`{{${v.key}}}`}</Label>
                                        <Input
                                            value={effectiveOverrides[v.key] ?? v.value}
                                            onChange={e => handleOverrideChange(v.key, e.target.value)}
                                            placeholder={`Valor para ${v.key}`}
                                            className="text-sm"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-red-600">{error}</p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleCopy}
                        disabled={loading || done || !canCopy}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {done
                            ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Copiado!</>
                            : loading
                                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Copiando...</>
                                : <><Copy className="h-4 w-4 mr-1" /> Copiar variáveis</>
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
